import axios, { AxiosRequestConfig } from 'axios';
import authService from '../authorization/AuthorizeService';
import { SortingType } from 'components/base-table/dto';

let isRefreshing = false;
let requestQueue: any[] = [];

const processQueue = (error: any, token: string | null) => {
    requestQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    requestQueue = [];
};

const axiosServices = axios.create({
    baseURL: process.env.REACT_APP_FUNCTION_API_URL,
    timeout: 90 * 1000,
    headers: {
        'Content-Type': 'application/json',
        'x-functions-key': process.env.REACT_APP_FUNCTION_API_KEY,
    },
});

export const setRequestInterceptors = (accessToken?: string | null) => {
    if (accessToken) {
        axiosServices.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
        delete axiosServices.defaults.headers.common.Authorization;
    }
};

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    bypassInterceptor?: boolean;
}

export const customAxiosService = (config: CustomAxiosRequestConfig) => {
    return axiosServices(config);
};

axiosServices.interceptors.request.use(
    async (request) => {
        if (('bypassInterceptor' in request && !request.bypassInterceptor) || !('bypassInterceptor' in request)) {
            const isExpired = await authService.isTokenExpired();
            if (isExpired && !isRefreshing) {
                isRefreshing = true;
                try {
                    await authService.tryRefreshToken(true);
                    const newToken = await authService.getAccessToken();
                    setRequestInterceptors(newToken);
                    request.headers.Authorization = `Bearer ${newToken}`;
                    processQueue(null, newToken);
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    throw refreshError;
                } finally {
                    isRefreshing = false;
                }
            } else if (isRefreshing) {
                // Wait for refresh to complete
                return new Promise((resolve, reject) => {
                    requestQueue.push({
                        resolve: (token: string) => {
                            request.headers.Authorization = `Bearer ${token}`;
                            resolve(request);
                        },
                        reject: (err: any) => reject(err),
                    });
                });
            } else {
                const token = await authService.getAccessToken();
                request.headers.Authorization = `Bearer ${token}`;
            }
        }

        return request;
    },
    (error) => {
        return Promise.reject(error);
    },
);

axiosServices.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.bypassInterceptor) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    await authService.tryRefreshToken(true);
                    const newToken = await authService.getAccessToken();
                    setRequestInterceptors(newToken);
                    processQueue(null, newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axiosServices(originalRequest);
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    // 🔴 Token refresh failed, redirect
                    window.location.href = '/unauthorized';
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // Wait for refresh to finish and retry
                return new Promise((resolve, reject) => {
                    requestQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(axiosServices(originalRequest));
                        },
                        reject: (err: any) => reject(err),
                    });
                });
            }
        }

        return Promise.reject((error.response && error.response) || 'Wrong Services');
    },
);

export default axiosServices;

export type PaginationAndFiltersParams = {
    pageIndex: number;
    pageSize: number;
    andSpecifications?: string;
    orSpecifications?: string;
    sortingType?: SortingType;
    sortBy?: string;
};

export type FiltersParams = {
    andSpecifications?: string;
    orSpecifications?: string;
};

export const buildQueryParams = (params: PaginationAndFiltersParams): string => {
    let query = `?pageNumber=${params.pageIndex + 1}&pageSize=${params.pageSize}`;

    if (params.sortingType && params.sortingType.length > 0) {
        query += `&sortingType=${params.sortingType}`;
    }

    if (params.sortBy && params.sortBy.length > 0) {
        query += `&sortBy=${params.sortBy}`;
    }

    if (params.andSpecifications && params.andSpecifications.length > 0) {
        query += `&andSpecifications=${params.andSpecifications}`;
    }

    if (params.orSpecifications && params.orSpecifications.length > 0) {
        query += `&orSpecifications=${params.orSpecifications}`;
    }

    return query;
};

export type FiltersAndSortingParams = FiltersParams & {
    sortingType?: SortingType;
    sortBy?: string;
};

export const buildFilterParams = (params?: FiltersAndSortingParams): string => {
    if (!params) return '';

    let query = `?`;

    if (params.andSpecifications && params.andSpecifications.length > 0) {
        query += `&andSpecifications=${params.andSpecifications}`;
    }

    if (params.orSpecifications && params.orSpecifications.length > 0) {
        query += `&orSpecifications=${params.orSpecifications}`;
    }

    if (params.sortingType && params.sortingType.length > 0) {
        query += `&sortingType=${params.sortingType}`;
    }

    if (params.sortBy && params.sortBy.length > 0) {
        query += `&sortBy=${params.sortBy}`;
    }

    return query;
};

export const AXIOS_REQUEST_TIMEOUT = 'ECONNABORTED';
